import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';

import { useFeatureToggle } from '@flopflip/react-redux';
import { Stack } from '@mui/material';
import InfoTitle from '@sharedCR/coreDesignSystem/InfoTitle';
import NoLoanProgramsPopup, {
  NoLoanProgramsPopupMessage,
} from 'app/pages/loanApplication/Validation/NoLoanProgramsPopup';
import LoanInfoModal from 'app/pages/loanApplication/Validation/restyled/LoanInfoModal';
import { applicationApi } from 'app/services/api';
import { ActionsFieldNames } from 'app/services/autoLockForms/autoLockForms.constants';
import axios from 'axios';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { FeatureFlag } from 'constants/featureFlags';
import { PROGRAM_FILTERS_GROUP_ID, SearchFilterConfigurationStatus } from 'constants/programs';
import useAbortController from 'hooks/useAbortController';
import useCompanyId from 'hooks/useCompanyId';
import { useEffectSafeStrictMode } from 'hooks/useEffectSafeStrictMode';
import { getDefaultErrorMessage } from 'utils/axios';
import { getConditionalObject } from 'utils/data';
import { logger } from 'utils/logger';
import { getIsBestPricingSearch } from 'utils/programs';

import { getFiltersGroupValuesSelector } from 'store/filters/selectors';
import { closeDialogAction, openDialogAction, showMessageAction } from 'store/global/actions';
import {
  getLoanOptionConfigurationAction,
  setLoanProgramOptions,
  setLoanProgramOptionsDetails,
} from 'store/loanApplications/actions';
import {
  selectCommonInfoLoanApplication,
  selectIsActionLocked,
  selectIsLoanNoPpeEngineType,
  selectIsOBEngineType,
  selectIsOBUiEngineType,
  selectLoanPricingEngineType,
  selectProgramAndPricingApplicationData,
} from 'store/loanApplications/selectors';

import { PropertyAndLoanTestIds } from '../../../../PropertyAndLoan.testIds';
import { defaultForm, pagesPropTypes } from '../MainContent.constants';
import OptimalBlueLaunchButton from '../OptimalBlueLaunchButton';

import { boxStyles } from './ProgramsAndPricing.styles';
import {
  getMemoizedLoanInfo,
  getSearchFilterRequest,
  getShouldShowNoProgramsPopup,
  trackSearchFilterConfigurationRequest,
} from './ProgramsAndPricing.utils';

const ProgramsAndPricing = ({
  isWithInfoTitle = true,
  isFromPropertyAndLoan = true,
  isProgramReselection = false,
  pendingChanges,
  isLoading,
  setIsLoading,
  setEntitySideData,
  setIsFormChanged = _.noop,
  isFormChanged,
  form = defaultForm,
  applicationData,
  loanOptionsSource,
}) => {
  const dispatch = useDispatch();
  const companyId = useCompanyId();
  const { applicationId } = useParams();

  const { getAbortSignal, resetAbortSignal } = useAbortController();

  const isLazyLoadPricingEnabled = useFeatureToggle(FeatureFlag.EnableLazyLoadPricing);
  const isOptionsDeduplicateEnabled = useFeatureToggle(
    FeatureFlag.DeduplicateRemoteLoanOptionsOnGroups,
  );

  const [tempRateSheetId, setTempRateSheetId] = useState(null);
  const [searchFilterConfigurationStatus, setSearchFilterConfigurationStatus] = useState(
    SearchFilterConfigurationStatus.LOADING,
  );

  const atParPayloadRef = useRef(null);
  const searchFilterConfigurationRequestIdRef = useRef(0);

  const loanApplication = useSelector(selectCommonInfoLoanApplication);
  const { programId: currentProgramId } = useSelector(selectProgramAndPricingApplicationData);
  const isLoanNoPpeEngineType = useSelector(selectIsLoanNoPpeEngineType);
  const pricingEngineType = useSelector(selectLoanPricingEngineType);
  const isOBEngineType = useSelector(selectIsOBEngineType);
  const isOBUiEngineType = useSelector(selectIsOBUiEngineType);

  const isLazyLoadEnabled = isLazyLoadPricingEnabled && isOBEngineType;

  const isProgramChangeLocked = useSelector(selectIsActionLocked(ActionsFieldNames.PROGRAM_CHANGE));
  const searchFilter = useSelector(
    getFiltersGroupValuesSelector({ filtersGroupId: PROGRAM_FILTERS_GROUP_ID }),
  );

  const { compensationType } = searchFilter || {};
  const rateSheetId = (tempRateSheetId || loanApplication.rateSheetId) ?? null;
  const { loanPurpose, additionalLoanSecondMortgageType } = applicationData || {};

  const isLoanLoaded = !!_.size(loanApplication);

  const loanInfoData = useMemo(
    () => getMemoizedLoanInfo({ form, isLoanNoPpeEngineType, applicationData }),
    [form, isLoanNoPpeEngineType, applicationData],
  );

  useEffect(() => {
    if (isLoanLoaded) {
      return;
    }

    setIsLoading(true);

    return () => setIsLoading(false);
  }, [isLoanLoaded, setIsLoading]);

  useEffectSafeStrictMode(() => {
    if (!isOBEngineType || !isLoanLoaded) {
      return;
    }

    trackSearchFilterConfigurationRequest({
      request: dispatch(
        getLoanOptionConfigurationAction({
          companyId,
          applicationId,
          pricingEngineType,
          isVirtualApplication: true,
          loanPurpose,
          additionalLoanSecondMortgageType,
        }),
      ),
      requestIdRef: searchFilterConfigurationRequestIdRef,
      setStatus: setSearchFilterConfigurationStatus,
    });
  }, [
    dispatch,
    companyId,
    applicationId,
    pricingEngineType,
    isLoanLoaded,
    loanPurpose,
    isOBEngineType,
    additionalLoanSecondMortgageType,
  ]);

  const getLoanOptions = useCallback(
    ({ searchFilter, isIneligibleRefetch = false }) => {
      if (!isLoanLoaded || (isWithInfoTitle && !loanInfoData)) {
        return;
      }

      resetAbortSignal();

      if (!isIneligibleRefetch) {
        setIsLoading(true);
      }

      const searchPayload = {
        ...getConditionalObject(rateSheetId, { pricing: { rateSheetId } }),
        loanOptionsFetchingOptions: {
          includeIneligibleLoanOptions: isIneligibleRefetch || !isLazyLoadEnabled,
        },
        ...getConditionalObject(isFormChanged, { loanInfo: loanInfoData }),
        ...getConditionalObject(loanOptionsSource, { source: loanOptionsSource }),
        searchFilter: getSearchFilterRequest({
          searchFilter,
          isLazyLoadEnabled,
          isOptionsDeduplicateEnabled,
        }),
      };

      const requestBody = {
        applicationId,
        signal: getAbortSignal(),
        ...searchPayload,
      };

      const apiCall = isLazyLoadEnabled
        ? applicationApi.getAtParLoanOptions(requestBody)
        : applicationApi.createLoanOptionsWithPending(requestBody);

      return apiCall
        .then(loanOptionsResponse => {
          const {
            loanOptions: loanProgramsOptions,
            details,
            engineType,
            lockedRateSheetId,
            suggestLoanOptionsRequestId: requestId,
            ineligibleOptionsData,
          } = loanOptionsResponse.data || {};

          const hasIneligibleOptions = !_.isEmpty(ineligibleOptionsData?.items);

          const isBestPricingSearch = getIsBestPricingSearch({
            searchFilter,
            isLazyLoadEnabled,
            isOptionsDeduplicateEnabled,
          });

          if (
            getShouldShowNoProgramsPopup({
              loanProgramsOptions,
              hasIneligibleOptions,
              isWithInfoTitle,
              isBestPricingSearch,
            })
          ) {
            dispatch(closeDialogAction());
            dispatch(
              openDialogAction({
                body: () => (
                  <NoLoanProgramsPopup message={NoLoanProgramsPopupMessage.NO_PROGRAMS} />
                ),
              }),
            );

            return true;
          }

          dispatch(setLoanProgramOptions(loanProgramsOptions));
          dispatch(
            setLoanProgramOptionsDetails({
              ...details,
              vendorType: engineType,
              loanProgramsOptions,
              ineligibleOptionsData,
            }),
          );

          atParPayloadRef.current = isLazyLoadEnabled
            ? {
                payload: searchPayload,
                requestId,
                loanProgramsOptions,
                ineligibleOptionsData,
                engineType,
              }
            : null;

          setEntitySideData(prevState => ({
            ...prevState,
            programsAndPricingContext: { rateSheetId, lockedRateSheetId, requestId },
          }));

          return true;
        })
        .catch(err => {
          const isRequestCancelled = axios.isCancel(err);

          if (!isRequestCancelled) {
            dispatch(showMessageAction({ message: getDefaultErrorMessage(err), variant: 'error' }));
            logger.error(err);
          }

          return false;
        })
        .finally(() => setIsLoading(false));
    },
    [
      dispatch,
      rateSheetId,
      applicationId,
      isWithInfoTitle,
      setIsLoading,
      isLoanLoaded,
      resetAbortSignal,
      getAbortSignal,
      setEntitySideData,
      loanInfoData,
      isFormChanged,
      loanOptionsSource,
      isLazyLoadEnabled,
      isOptionsDeduplicateEnabled,
    ],
  );

  useEffectSafeStrictMode(() => {
    if (isOBEngineType || isOBUiEngineType || !compensationType) {
      return;
    }

    getLoanOptions({ searchFilter: { compensationType } });
  }, [getLoanOptions, isOBEngineType, isOBUiEngineType, compensationType]);

  const canSelectProgram = useCallback(
    program => {
      if (!isProgramChangeLocked) {
        return true;
      }

      return program?.programId === currentProgramId;
    },
    [isProgramChangeLocked, currentProgramId],
  );

  const onSelectProgram = useCallback(
    selectedProgram => {
      if (!selectedProgram) {
        return;
      }

      if (!canSelectProgram(selectedProgram)) {
        return;
      }

      setEntitySideData(prevState => ({
        ...prevState,
        selectedProgram,
      }));

      setIsFormChanged(true);
    },
    [setEntitySideData, setIsFormChanged, canSelectProgram],
  );

  return (
    <Stack
      data-testid={PropertyAndLoanTestIds.LISTING_AGENT_FORM_PROGRAMS_AND_PRICING}
      sx={boxStyles}
    >
      {isWithInfoTitle && <InfoTitle title="Programs & Pricing" />}

      <OptimalBlueLaunchButton isHidden={!isOBUiEngineType} />

      {!isOBUiEngineType && (
        <LoanInfoModal
          atParPayloadRef={atParPayloadRef}
          canSelectProgram={canSelectProgram}
          getLoanOptions={getLoanOptions}
          isFromPropertyAndLoan={isFromPropertyAndLoan}
          isLoading={isLoading}
          isProgramReselection={isProgramReselection}
          pendingChanges={pendingChanges}
          rateSheetId={rateSheetId}
          searchFilterConfigurationStatus={searchFilterConfigurationStatus}
          setIsLoading={setIsLoading}
          setTempRateSheetId={setTempRateSheetId}
          onSelectProgram={onSelectProgram}
        />
      )}
    </Stack>
  );
};

ProgramsAndPricing.propTypes = {
  ...pagesPropTypes,
  isProgramReselection: PropTypes.bool,
};

export default ProgramsAndPricing;
